import { useState, useCallback, useRef } from "react";
import { getAuthHeaders } from "../../auth/services/getAuthHeaders";

export function useSummaryEmail(summaryServiceUrl: string, currentMeetingId: string | null) {
  const [emailInput, setEmailInput] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState<"idle" | "success" | "error">("idle");
  const [emailMessage, setEmailMessage] = useState("");
  const [emptyFieldError, setEmptyFieldError] = useState(false);
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showTemporaryMessage = useCallback((status: "success" | "error", message: string) => {
    setEmailStatus(status);
    setEmailMessage(message);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setEmailStatus("idle");
      setEmailMessage("");
    }, 3000);
  }, []);

  const handleSendEmail = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const emails = emailInput
      .split(",")
      .map((e) => e.trim())
      .filter((e) => e);

    if (emails.length === 0) {
      setEmptyFieldError(true);
      emailInputRef.current?.focus();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setEmptyFieldError(false);
      }, 3000);
      return;
    }
    setEmptyFieldError(false);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const email of emails) {
      if (!emailRegex.test(email)) {
        showTemporaryMessage("error", `El correo "${email}" no es válido.`);
        return;
      }
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setEmailLoading(true);
    setEmailStatus("idle");

    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${summaryServiceUrl}/meeting/email`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          meeting_id: currentMeetingId,
          email: emails.join(","),
        }),
      });

      if (!response.ok) {
        throw new Error("Error en la llamada de email del servidor");
      }

      showTemporaryMessage("success", `¡Minuta enviada correctamente a ${emails.join(", ")}!`);
      setEmailInput("");
    } catch (err) {
      console.error(err);
      showTemporaryMessage("error", "No se pudo enviar el correo. Revisa la configuración del servidor.");
    } finally {
      setEmailLoading(false);
    }
  }, [emailInput, currentMeetingId, summaryServiceUrl, showTemporaryMessage]);

  const resetEmailState = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setEmailInput("");
    setEmailStatus("idle");
    setEmailMessage("");
    setEmptyFieldError(false);
  }, []);

  return {
    emailInput,
    setEmailInput,
    emailLoading,
    emailStatus,
    emailMessage,
    emptyFieldError,
    setEmptyFieldError,
    emailInputRef,
    handleSendEmail,
    resetEmailState,
  };
}
